<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useKanbanStagesStore } from 'dashboard/stores/kanbanStages';
import KanbanColumn from './KanbanColumn.vue';

const props = defineProps({
  pipelineId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['editStage']);

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const kanbanStagesStore = useKanbanStagesStore();

const stages = computed(() =>
  kanbanStagesStore.getStagesByPipeline(props.pipelineId)
);

const loadBoard = async () => {
  try {
    await kanbanStagesStore.getBoard(props.pipelineId);
  } catch (error) {
    useAlert(t('KANBAN.BOARD.LOAD_ERROR_MESSAGE'));
  }
};

onMounted(loadBoard);
watch(() => props.pipelineId, loadBoard);

const handleCardDropped = async ({ conversation, toStageId }) => {
  if (Number(conversation.kanban_stage_id) === Number(toStageId)) return;
  try {
    await kanbanStagesStore.moveConversationToStage({
      conversation,
      fromStageId: conversation.kanban_stage_id,
      toStageId,
    });
  } catch (error) {
    useAlert(t('KANBAN.BOARD.MOVE_ERROR_MESSAGE'));
  }
};

const handleLoadMore = async stage => {
  try {
    await kanbanStagesStore.loadMoreConversations({
      pipelineId: props.pipelineId,
      id: stage.id,
    });
  } catch (error) {
    useAlert(t('KANBAN.BOARD.LOAD_MORE_ERROR_MESSAGE'));
  }
};

const handleOpenConversation = conversation => {
  router.push(
    accountScopedRoute('inbox_conversation', {
      conversation_id: conversation.id,
    })
  );
};
</script>

<template>
  <div class="flex h-full gap-3 overflow-x-auto">
    <KanbanColumn
      v-for="stage in stages"
      :key="stage.id"
      :stage="stage"
      :conversations="kanbanStagesStore.getConversationsForStage(stage.id)"
      :is-loading-more="
        kanbanStagesStore.loadingMoreStageIds.includes(stage.id)
      "
      @card-dropped="handleCardDropped"
      @load-more="handleLoadMore(stage)"
      @open-conversation="handleOpenConversation"
      @edit-stage="emit('editStage', $event)"
    />
  </div>
</template>
